import { useState, useId } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../lib/api';
import { fmt } from '../lib/format';
import { when } from '../lib/labels';
import { EVENT_GROUPS, EVENT_TYPES } from '../lib/events';
import { Dialog, ErrorBox, Load, Section, Table } from '../ui/kit';

// API keys: a form with the key's mode, lifetime, scopes and (live) spend caps, and a panel that shows a new
// secret once, with a copy button. Webhooks: a form with the events picked from a list, and each one's delivery log.

import { ALL, PRESETS, RESOURCES, SPECIAL } from '../lib/scopes';
export { PRESETS };

const LIFETIMES: Array<[string, string, number | null]> = [['30d', '30 days', 30], ['90d', '90 days', 90], ['1y', '1 year', 365], ['never', 'Never expires', null]];

/** The expiry for a lifetime choice, or undefined for a key that never expires. */
export const expiresAt = (choice: string, now = Date.now()) => {
  const days = LIFETIMES.find(([k]) => k === choice)?.[2];
  return days ? new Date(now + days * 86_400_000).toISOString() : undefined;
};

export function NewKeyButton({ onCreated }: { onCreated: (secret: string, name: string) => void }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'test' | 'live'>('test');
  const [scopes, setScopes] = useState<Set<string>>(new Set(PRESETS.integration!.scopes));
  const qc = useQueryClient();
  const m = useMutation<{ secret: string; name: string }, ApiError, Record<string, unknown>>({
    mutationFn: (body) => api('/portal/api-keys', { method: 'POST', body }),
    onSuccess: (r) => { setOpen(false); void qc.invalidateQueries(); onCreated(r.secret, r.name); },
  });
  const toggle = (s: string, on: boolean) => setScopes((cur) => { const n = new Set(cur); if (on) n.add(s); else n.delete(s); return n; });
  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    m.mutate({
      name: f.get('name'), mode, scopes: ALL.filter((s) => scopes.has(s)), user_bound: f.get('user_bound') === 'on',
      ...(expiresAt(String(f.get('lifetime'))) ? { expires_at: expiresAt(String(f.get('lifetime'))) } : {}),
      ...(mode === 'live' ? { caps: { day_cap: Number(f.get('day_cap')), month_cap: Number(f.get('month_cap')), per_call_max: Number(f.get('per_call_max')) } } : {}),
    });
  };
  return (
    <>
      <button type="button" className="btn btn-primary" onClick={() => { m.reset(); setOpen(true); }}>New API key</button>
      <Dialog title="New API key" open={open} onClose={() => setOpen(false)}>
        <form onSubmit={submit} className="form key-form">
          <div className="field"><label htmlFor="k-name">Name</label><input id="k-name" name="name" required maxLength={100} placeholder="e.g. Botscope backend" /></div>
          <fieldset className="field">
            <legend>Mode</legend>
            <div className="seg" role="radiogroup">
              <label><input id={`${uid}-f1`} type="radio" name="mode" checked={mode === 'test'} onChange={() => setMode('test')} /> Test <span className="muted small">sandbox, fake data, no credits</span></label>
              <label><input id={`${uid}-f2`} type="radio" name="mode" checked={mode === 'live'} onChange={() => setMode('live')} /> Live <span className="muted small">your real data; server-side only</span></label>
            </div>
          </fieldset>
          <div className="field"><label htmlFor="k-life">Expires</label>
            <select id="k-life" name="lifetime" defaultValue="90d">{LIFETIMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            <div className="muted small">You can revoke a key at any time, whatever its expiry.</div>
          </div>
          <fieldset className="field">
            <legend>Scopes <span className="muted small">({scopes.size} selected)</span></legend>
            <div className="presets">{Object.entries(PRESETS).map(([k, p]) => <button key={k} type="button" className="btn btn-sm" onClick={() => setScopes(new Set(p.scopes))}>{p.label}</button>)}
              <button type="button" className="btn btn-sm" onClick={() => setScopes(new Set())}>Clear</button></div>
            <table className="scope-grid">
              <thead><tr><th scope="col">Resource</th><th scope="col">Read</th><th scope="col">Write</th></tr></thead>
              <tbody>{RESOURCES.map((r) => (
                <tr key={r.key}><th scope="row">{r.label}</th>
                  {(['read', 'write'] as const).map((a) => {
                    const desc = r[a];
                    const s = `${r.key}:${a}`;
                    return (
                      <td key={a}>
                        <label className={`scope ${desc ? '' : 'none'}`}>
                          <input id={`${uid}-${s}`} type="checkbox" disabled={!desc} checked={Boolean(desc) && scopes.has(s)} onChange={(e) => toggle(s, e.target.checked)} />
                          <span><code>{s}</code><span className="desc">{desc ?? `Nothing to ${a} here`}</span></span>
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}</tbody>
            </table>
            {SPECIAL.map(([s, label]) => <label key={s} className="scope special"><input id={`${uid}-special-${s}`} type="checkbox" checked={scopes.has(s)} onChange={(e) => toggle(s, e.target.checked)} /><span><code>{s}</code><span className="desc">{label}</span></span></label>)}
          </fieldset>
          <label className="check"><input id={`${uid}-f5`} type="checkbox" name="user_bound" /> Acts as me <span className="muted small">needed for campaigns, for accepting or rejecting handoffs, for connecting ad accounts and for ads:manage</span></label>
          {mode === 'live' ? (
            <fieldset className="field">
              <legend>Spend caps (credits)</legend>
              <div className="caps">
                <label>Per day <input id={`${uid}-f6`} type="number" name="day_cap" min={0} required defaultValue={100} /></label>
                <label>Per month <input id={`${uid}-f7`} type="number" name="month_cap" min={0} required defaultValue={1000} /></label>
                <label>Per call <input id={`${uid}-f8`} type="number" name="per_call_max" min={0} required defaultValue={30} /></label>
              </div>
              <div className="muted small">Creating a live key needs a second factor in the last 12 hours.</div>
            </fieldset>
          ) : null}
          {m.error ? <ErrorBox error={m.error} /> : null}
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => setOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={m.isPending || !scopes.size}>{m.isPending ? 'Creating…' : `Create ${mode} key`}</button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

/** A secret shown once: large, selectable, with a copy button. */
export function SecretReveal({ title, secret, onDone }: { title: string; secret: string; onDone: () => void }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(secret); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* the field stays selectable */ }
  };
  return (
    <section className="secret" role="alert" aria-label={title}>
      <h2>{title}</h2>
      <p>Copy it now and keep it in your server's environment (for example <code>OUTLEVO_KEY</code>). It is not shown again.</p>
      <div className="secret-row">
        <input id={`${uid}-f9`} className="secret-value" readOnly value={secret} aria-label="Secret" onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="btn btn-primary" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <button type="button" className="btn" onClick={onDone}>I have stored it</button>
    </section>
  );
}

/** A webhook subscription: an HTTPS address and the events it gets, picked from the list the API accepts. */
export function NewWebhookButton({ onCreated }: { onCreated: (secret: string) => void }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<Set<string>>(new Set(['handoff.created', 'job.completed']));
  const qc = useQueryClient();
  const m = useMutation<{ secret: string }, ApiError, { url: string; events: string[] }>({
    mutationFn: (body) => api('/v1/webhooks', { method: 'POST', body }),
    onSuccess: (r) => { setOpen(false); void qc.invalidateQueries(); onCreated(r.secret); },
  });
  const toggle = (e: string, on: boolean) => setEvents((cur) => { const n = new Set(cur); if (on) n.add(e); else n.delete(e); return n; });
  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    m.mutate({ url: String(new FormData(e.currentTarget).get('url')), events: EVENT_TYPES.filter((x) => events.has(x)) });
  };
  return (
    <>
      <button type="button" className="btn" onClick={() => { m.reset(); setOpen(true); }}>Add webhook</button>
      <Dialog title="Subscribe a webhook" open={open} onClose={() => setOpen(false)}>
        <form onSubmit={submit} className="form key-form">
          <div className="field"><label htmlFor="wh-url">HTTPS URL</label><input id="wh-url" name="url" type="url" required placeholder="https://hooks.example.com/outlevo" /></div>
          <fieldset className="field">
            <legend>Events <span className="muted small">({events.size} selected, up to 30)</span></legend>
            <div className="presets">
              <button type="button" className="btn btn-sm" onClick={() => setEvents(new Set(EVENT_TYPES.slice(0, 30)))}>First 30</button>
              <button type="button" className="btn btn-sm" onClick={() => setEvents(new Set())}>Clear</button>
            </div>
            {EVENT_GROUPS.map((g) => (
              <fieldset key={g.label} className="event-group">
                <legend className="small">{g.label}</legend>
                {g.events.map(([ev, desc]) => (
                  <label key={ev} className="scope">
                    <input id={`${uid}-${ev}`} type="checkbox" checked={events.has(ev)} onChange={(x) => toggle(ev, x.target.checked)} />
                    <span><code>{ev}</code><span className="desc">{desc}</span></span>
                  </label>
                ))}
              </fieldset>
            ))}
          </fieldset>
          {m.error ? <ErrorBox error={m.error} doing="add the webhook" /> : null}
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => setOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={m.isPending || !events.size || events.size > 30}>{m.isPending ? 'Adding…' : 'Add webhook'}</button>
          </div>
        </form>
      </Dialog>
    </>
  );
}

type Delivery = { id: string; type: string; attempt: number; status: string; status_code: number | null; latency_ms: number | null; error: string | null; next_attempt_at: string | null; delivered_at: string | null; created_at: string };

/** One subscription's delivery attempts, newest first, 50 at a time. Failed deliveries retry on their own (1 min to 12 h). */
export function DeliveryLog({ id, url, onClose }: { id: string; url: string; onClose: () => void }) {
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  return (
    <Section title={`Deliveries to ${url}`} actions={<button type="button" className="btn" onClick={onClose}>Close</button>}>
      <p className="muted small">Kept 30 days. A failed attempt is retried after 1 minute, 5 minutes, 30 minutes, 2 hours and 12 hours, then marked failed. Each delivered event costs 1 credit.</p>
      <Load<{ data: Delivery[]; next_cursor: string | null }> path={`/v1/webhooks/${id}/deliveries?limit=50${cursor ? `&cursor=${cursor}` : ''}`}>{(d) => (
        <>
          <Table caption="Webhook deliveries" rows={d.data} columns={[{ key: 'type', label: 'Event', render: (r) => <code>{r.type}</code> }, 'status', { key: 'attempt', label: 'Attempts', render: (r) => fmt(r.attempt) },
            { key: 'status_code', label: 'Response', render: (r) => (r.status_code ? `HTTP ${r.status_code}` : r.error ?? '—') }, { key: 'latency_ms', label: 'Time taken', render: (r) => (r.latency_ms === null ? '—' : `${r.latency_ms} ms`) },
            { key: 'created_at', label: 'When', render: (r) => when(r.created_at) }, { key: 'next_attempt_at', label: 'Next try', render: (r) => (r.status === 'pending' && r.next_attempt_at ? when(r.next_attempt_at) : '—') }]}
            empty="No deliveries yet. Send a test to check the endpoint." />
          <span className="actions">
            {cursors.length ? <button type="button" className="btn" onClick={() => setCursors(cursors.slice(0, -1))}>Newer</button> : null}
            {d.next_cursor ? <button type="button" className="btn" onClick={() => setCursors([...cursors, d.next_cursor!])}>Older</button> : null}
          </span>
        </>
      )}</Load>
    </Section>
  );
}
